import { slotDescription } from './sitedata/space.js';
import { toSlotKey, toSlotKeyDecoded } from './slotKey.js';
import { timeSiteNow, timeSiteParse } from './time.js';
import { emptyUser } from './user.js';
import { zeroPad } from './utils/utils.js';
const emptyUnitPrice = () => {
    return {
        basePrice: NaN,
        finalPrice: NaN,
        punchCardId: '',
        clubCodeId: ''
    };
};
var TicketState;
(function (TicketState) {
    // The seat is available for booking now (for tickets that are cancelled for instance)
    TicketState["available"] = "available";
    // The seat is in an order which is being processed
    TicketState["reserved"] = "reserved";
    // The seat is in an order which has been approved by payment site
    TicketState["booked"] = "booked";
    // unavailable, used for linked tickets
    TicketState["unavailable"] = "unavailable";
    // And then all other states (for initialization and really an error state)
    TicketState["unknown"] = "unknown";
    TicketState["cancelled"] = "cancelled";
})(TicketState || (TicketState = {}));
const emptyTicket = () => {
    return {
        ticketKey: '',
        orderId: '',
        state: TicketState.unknown,
        freeText: '',
        description: '',
        addedAt: '',
        reservedAt: '',
        bookedAt: '',
        groupBracket: [],
        extras: [],
        activityName: '',
        totalPrice: NaN,
        unitPrice: emptyUnitPrice(),
        groupPrice: NaN,
        user: emptyUser(),
        holderPublicName: '',
        clubCodeId: '',
        punchCardId: ''
    };
};
// dummy comment to trigger full rebuild
const baseTicket = (slotKey, totalPrice, ticketNo = 1) => {
    const ticket = emptyTicket();
    ticket.ticketKey = toTicketKey(slotKey + '.' + ticketNo);
    ticket.totalPrice = totalPrice;
    ticket.unitPrice.basePrice = totalPrice;
    ticket.unitPrice.finalPrice = totalPrice;
    return ticket;
};
const emptyTicketPublic = () => {
    return toTicketPublic(emptyTicket());
};
const toTicket = (body, itemKey) => {
    const ticket = emptyTicket();
    if (!body)
        return ticket;
    ticket.ticketKey = body.ticketKey ?? `${itemKey ? toSlotKey(itemKey) : ''}.1`;
    ticket.orderId = body.orderId ?? ticket.orderId;
    ticket.state = body.state ?? ticket.state;
    ticket.freeText = body.freeText ?? body.reference ?? ticket.freeText;
    ticket.description = body.description ?? ticket.description;
    ticket.addedAt = body.addedAt ?? ticket.addedAt;
    ticket.bookedAt = body.bookedAt ?? ticket.bookedAt;
    ticket.reservedAt = body.reservedAt ?? ticket.reservedAt;
    // The mutable fields are COPIED, not aliased. Callers use toTicket as a copy
    // constructor - reserveSlots hands the same ticket to the cart, the calendar
    // and the order document and then edits prices on it - and a reference copy
    // made those writes land in every one of them, including the client-owned
    // cart. Shallow spreads rather than a deep clone: this runs per ticket while
    // building a month of live slots. `discount` is only copied when present, so
    // no undefined key is introduced for Firestore to reject.
    ticket.groupBracket = body.groupBracket
        ? [...body.groupBracket]
        : ticket.groupBracket;
    ticket.extras = body.extras
        ? body.extras.map((extra) => ({ ...extra }))
        : ticket.extras;
    ticket.activityName = body.activityName ?? ticket.activityName;
    ticket.totalPrice = body.totalPrice ?? body.price ?? ticket.totalPrice;
    if (body.unitPrice) {
        ticket.unitPrice = { ...body.unitPrice };
        if (body.unitPrice.discount) {
            ticket.unitPrice.discount = { ...body.unitPrice.discount };
        }
    }
    ticket.groupPrice = body.groupPrice ?? ticket.groupPrice;
    ticket.user = body.user ?? ticket.user;
    ticket.holderPublicName = body.holderPublicName ?? ticket.holderPublicName;
    ticket.clubCodeId = body.clubCodeId ?? ticket.clubCodeId;
    ticket.punchCardId = body.punchCardId ?? ticket.punchCardId;
    return ticket;
};
const toTicketPublic = (raw, itemKey) => {
    const full = toTicket(raw, itemKey);
    return (({ ticketKey, description, orderId, state, freeText, addedAt, reservedAt, bookedAt, groupBracket, extras, activityName, holderPublicName }) => ({
        ticketKey,
        description,
        orderId,
        state,
        freeText,
        addedAt,
        reservedAt,
        bookedAt,
        groupBracket,
        extras,
        activityName,
        holderPublicName
    }))(full);
};
const ticketToSlotKey = (ticketOrKey) => {
    if (typeof ticketOrKey !== 'string') {
        ticketOrKey = ticketOrKey.ticketKey;
    }
    return toSlotKey(ticketOrKey.split('.')[0]);
};
const toTicketKeyDecoded = (ticketKey) => {
    const split = ticketKey.split('.');
    return {
        ...toSlotKeyDecoded(toSlotKey(split[0])),
        ticketId: parseInt(split[1])
    };
};
const ticketKeyToYearMonth = (ticketKey) => {
    const item = toTicketKeyDecoded(ticketKey);
    return `${item.year}-${zeroPad(item.month)}`;
};
const ticketKeyRegEx = /[a-z0-9-]*-[0-9]{4}-[0-9]{2}-[0-9]{2}-[0-9]{2,4}\.[0-9]*/;
const toTicketKey = (ticket) => {
    if (typeof ticket === 'string') {
        if (!ticketKeyRegEx.test(ticket)) {
            throw Error('Ugyldig nøkkel: ' + ticket);
        }
    }
    const decoded = typeof ticket === 'string'
        ? toTicketKeyDecoded(ticket)
        : ticket;
    const slotKey = toSlotKey(decoded);
    return `${slotKey}.${decoded.ticketId}`;
};
const toTicketKey2 = (slotKey, ticketId) => {
    return `${slotKey}.${ticketId}`;
};
const ticketIsReservedButExpired = (ticket, slackInMinutes = 1) => {
    if (!ticket)
        return false;
    if (ticket.state === TicketState.reserved && ticket.reservedAt) {
        if (timeSiteParse(ticket.reservedAt).plus({ minutes: 10 + slackInMinutes }) <
            timeSiteNow()) {
            return true;
        }
    }
    return false;
};
const ticketState = (ticket) => {
    return ticketIsReservedButExpired(ticket)
        ? TicketState.available
        : ticket.state;
};
const ticketGroupBracketToString = (bracket) => {
    if (!bracket || bracket.length === 0)
        return '';
    if (bracket.length === 1 || isNaN(bracket[1]))
        return `Gruppe på ${bracket[0]}+`;
    if (bracket.length === 2) {
        if (bracket[0] === 0)
            return `Gruppe under ${bracket[1]}`;
        if (bracket[0] === bracket[1] || bracket[0] === bracket[1] - 1)
            return `Gruppe på ${bracket[0]}`;
        return `Gruppe fra ${bracket[0]} til ${bracket[1] - 1}`;
    }
    return '';
};
const ticketDescription = (ticket, siteData) => {
    const postGroup = ticketGroupBracketToString(ticket.groupBracket);
    const defaultAName = siteData.activities.find((activity) => activity.id === '')?.name ?? '';
    const postAName = ticket.activityName === defaultAName || !ticket.activityName
        ? ''
        : ` [${ticket.activityName}]`;
    const slot = toTicketKeyDecoded(ticket.ticketKey); // ticket decoded  is a superset of slot
    const slotDesc = slotDescription(slot, siteData);
    return `${slotDesc}${postAName}` + (postGroup ? ` (${postGroup})` : '');
};
export { baseTicket, emptyTicket, emptyTicketPublic, emptyUnitPrice, ticketDescription, ticketGroupBracketToString, ticketIsReservedButExpired, ticketKeyToYearMonth, ticketState, TicketState, ticketToSlotKey, toTicket, toTicketKey, toTicketKey2, toTicketPublic, toTicketKeyDecoded };
//# sourceMappingURL=ticket.js.map